import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Input,
  List,
  ListDivider,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Stack,
  Typography,
} from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import {
  EventParticipant,
  fetchAdminEventParticipants,
} from "../../api";

interface Props {
  eventId: number | string;
  eventTitle?: string;
  eventHead?: string;
  loadingEvent?: boolean;
}

export interface AdminEventParticipantsHandle {
  refresh: () => void;
}

const getInitialState = () => ({
  loading: true,
  refreshing: false,
});

const AdminEventParticipants = React.forwardRef<
  AdminEventParticipantsHandle,
  Props
>(function AdminEventParticipants(
  { eventId, eventTitle, eventHead, loadingEvent },
  ref,
) {
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [state, setState] = useState(getInitialState);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadParticipants = useCallback(
    async (mode: "initial" | "refresh" = "initial") => {
      if (!eventId) {
        setParticipants([]);
        setState({ loading: false, refreshing: false });
        setError(
          "We need an event id to load the devotees supporting this gathering.",
        );
        return;
      }

      setError(null);
      if (mode === "initial") {
        setState({ loading: true, refreshing: false });
      } else {
        setState({ loading: false, refreshing: true });
      }

      try {
        const data = await fetchAdminEventParticipants(eventId);
        setParticipants(data ?? []);
        setLastUpdated(new Date());
      } catch (err) {
        console.error("Failed to fetch event participants", err);
        setError("We couldn’t load the roster. Please try refreshing.");
      } finally {
        setState({ loading: false, refreshing: false });
      }
    },
    [eventId],
  );

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => loadParticipants("refresh"),
    }),
    [loadParticipants],
  );

  const filteredParticipants = useMemo(() => {
    const safeQuery = query.trim().toLowerCase();
    if (!safeQuery) {
      return participants;
    }
    return participants.filter((participant) =>
      participant.fullName?.toLowerCase().includes(safeQuery),
    );
  }, [participants, query]);

  const { loading, refreshing } = state;
  const totalCount = participants.length;
  const showingCount = filteredParticipants.length;
  const searchActive = query.trim().length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        gap: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            {eventTitle ? `Devotees registered for ${eventTitle}` : "Registered devotees"}
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 520 }}>
            Celebrate everyone who has committed to this festival and keep the roster current so no soul is overlooked.
          </Typography>
          {eventHead ? (
            <Chip size="sm" variant="soft" color="primary" sx={{ alignSelf: "flex-start" }}>
              Coordinated by {eventHead}
            </Chip>
          ) : null}
          {loadingEvent ? (
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Updating event details…
            </Typography>
          ) : null}
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          width={{ xs: "100%", sm: "auto" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Input
            size="sm"
            placeholder="Search devotees"
            startDecorator={<SearchRoundedIcon />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              backgroundColor: "background.surface",
            }}
          />
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshRoundedIcon />}
            onClick={() => loadParticipants("refresh")}
            loading={refreshing}
            disabled={!eventId}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
        <Chip size="sm" variant="soft" color="primary">
          {totalCount} {totalCount === 1 ? "devotee" : "devotees"}
        </Chip>
        {searchActive ? (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Showing {showingCount} result{showingCount === 1 ? "" : "s"} for “{query.trim()}”.
          </Typography>
        ) : lastUpdated ? (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        ) : null}
      </Stack>

      {error ? (
        <Typography
          level="body-sm"
          color="danger"
          sx={{ bgcolor: "danger.softBg", borderRadius: "lg", p: 1.5 }}
        >
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size="md" color="primary" />
        </Box>
      ) : showingCount > 0 ? (
        <List sx={{ borderRadius: "lg", overflow: "hidden" }}>
          {filteredParticipants.map((participant, index) => {
            const initials = participant.fullName?.[0]?.toUpperCase() ?? "D";
            return (
              <React.Fragment key={participant.userId}>
                <ListItem>
                  <ListItemDecorator>
                    <Avatar variant="soft" color="primary">
                      {initials}
                    </Avatar>
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {participant.fullName}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      Devotee ID: {participant.userId}
                    </Typography>
                  </ListItemContent>
                  <Chip size="sm" variant="soft" color="success">
                    Registered
                  </Chip>
                </ListItem>
                {index < filteredParticipants.length - 1 ? (
                  <ListDivider inset="gutter" />
                ) : null}
              </React.Fragment>
            );
          })}
        </List>
      ) : totalCount === 0 ? (
        <Card
          variant="soft"
          color="neutral"
          sx={{ textAlign: "center", borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 1 }}
        >
          <Typography level="title-md" sx={{ fontWeight: 600 }}>
            No devotees have registered yet
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 440, mx: "auto" }}>
            Invite devotees to join {eventTitle ? `${eventTitle}` : "this event"}. The first registration will set a joyful tone for the celebration.
          </Typography>
        </Card>
      ) : (
        <Card
          variant="outlined"
          color="neutral"
          sx={{ textAlign: "center", borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 1 }}
        >
          <Typography level="title-md" sx={{ fontWeight: 600 }}>
            No matches found
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 440, mx: "auto" }}>
            Try refining your search or clearing the filter to see every devotee supporting this seva.
          </Typography>
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            onClick={() => setQuery("")}
            sx={{ alignSelf: "center", mt: 1 }}
          >
            Clear search
          </Button>
        </Card>
      )}
    </Card>
  );
});

export default AdminEventParticipants;

